import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, AuthPage } from '@features/auth';

interface AuthGuardProps {
  children: ReactNode;
}

const SESSION_INVITE_KEY = 'trenora.pendingInvite';

// Stash an invite code from /i/$code into sessionStorage as early as possible,
// before auth resolves. This way it survives the signup/login round-trip and
// InvitesCard can pre-fill the accept form when the user lands on /profile.
function capturePendingInviteFromUrl(): string | null {
  const match = /^\/i\/([^/?#]+)/.exec(window.location.pathname);
  const code = match?.[1] ?? null;
  if (code) {
    const upper = code.toUpperCase();
    window.sessionStorage.setItem(SESSION_INVITE_KEY, upper);
    return upper;
  }
  return window.sessionStorage.getItem(SESSION_INVITE_KEY);
}

/**
 * Renders children only when authenticated. Shows AuthPage otherwise.
 * Used by the router root so that bottom nav and pages are hidden until login.
 */
export function AuthGuard({ children }: AuthGuardProps): React.JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    capturePendingInviteFromUrl();
  }, []);

  if (auth.status === 'loading') {
    return (
      <main className="text-muted-foreground flex min-h-full items-center justify-center">
        {t('app.loading')}
      </main>
    );
  }

  if (auth.status === 'unauthenticated') {
    const pendingCode = capturePendingInviteFromUrl();
    return <AuthPage pendingInviteCode={pendingCode} />;
  }

  return <>{children}</>;
}
